
import { Component, Vue, Watch } from 'vue-property-decorator';

import SelectField from '../../../../framework/fields/SelectField.vue';
import { sharedState } from '../../../../framework/state';
import { deleteResource, getResourcePagingNew, getResourcesNew } from '../../../../framework/client/resource';
import { getEnumerationOptions, getOptionLabel } from '../../../../framework/service/options';

import ParameterService from '../../../service/parameter_service';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import { FinvoiceParserPattern } from '../../../../../common/application/model/FinvoiceParserPattern';
import { FinvoiceParserPatternType } from '../../../../../common/application/model/enums/FinvoiceParserPatternType';
import { errorToast, successToast } from '../../../service/toast_service';
import { dateToUiDateTimeString } from '../../../../../common/framework/util/convert';

@Component({
    components: {
        SelectField,
    },
})
export default class FinvoiceParserPatterns extends Vue {
    readonly resourceType = ApplicationResource.FINVOICE_PARSER_PATTERN;
    parameterService = new ParameterService(this.resourceType);

    shared = sharedState;
    local = {
        rows: [] as FinvoiceParserPattern[],
        total: 0,
        loading: false,
        selected: undefined as FinvoiceParserPattern | undefined,
        finvoiceParserPatternTypeOptions: [] as { id: string | undefined; label: string }[],
        parameters: this.parameterService.get(),
    };

    async mounted() {
        this.local.finvoiceParserPatternTypeOptions = getEnumerationOptions(
            'FinvoiceParserPatternType',
            Object.keys(FinvoiceParserPatternType),
            this,
        );
        await this.loadAsyncData();
    }

    async loadAsyncData() {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();
        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters, undefined)).rowCount;

        this.local.rows = [];

        const rows: FinvoiceParserPattern[] = (await getResourcesNew(
            this.resourceType,
            this.local.parameters.page,
            backendParameters,
        )) as FinvoiceParserPattern[];

        rows.forEach((row) => {
            this.local.rows.push(row);
        });

        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }

        this.local.loading = false;
    }

    add() {
        this.$router.push(`/${ApplicationResource.FINVOICE_PARSER_PATTERN}/add`);
    }

    edit(id?: string) {
        if (id) {
            this.$router.push(`/${ApplicationResource.FINVOICE_PARSER_PATTERN}/${id}/edit`);
        } else if (this.local.selected) {
            this.$router.push(`/${ApplicationResource.FINVOICE_PARSER_PATTERN}/${this.local.selected.id}/edit`);
        }
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    async onSortChange(field: string, order: 'desc' | 'asc') {
        this.local.parameters.sortField = field;
        this.local.parameters.sortOrder = order;
        await this.resetPagingAndLoadData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    @Watch('local.parameters.type')
    async selectedFinvoiceParserPatternTypeChange() {
        if (this.local.loading) {
            return;
        }

        await this.resetPagingAndLoadData();
    }

    async resetFilter(): Promise<void> {
        this.local.parameters = this.parameterService.getDefault();
        await this.resetPagingAndLoadData();
    }

    getFinvoiceParserPatternTypeLabel(id: string): string {
        return getOptionLabel(id, this.local.finvoiceParserPatternTypeOptions);
    }

    onRowClick(row: FinvoiceParserPattern) {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    formatDate(date: Date) {
        return dateToUiDateTimeString(new Date(date));
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
